import React, { useEffect, useRef } from "react";
import { Link } from "gatsby";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Container, Divider, Grid, IconButton, Stack, Typography, useMediaQuery } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube } from "react-icons/fa";
import { company } from "../config/company";
import { footerNavigation, secondaryFooterCategories, serviceCategories } from "../config/navigation";
import WeatherClockWidget from "./WeatherClockWidget";
import "./footer.css";

const socialIcons = { LinkedIn: FaLinkedin, Instagram: FaInstagram, Facebook: FaFacebook, YouTube: FaYoutube };
const allCategories = [...serviceCategories, ...secondaryFooterCategories];

function FooterLinks({ group }) {
  return (
    <Stack spacing={0.8}>
      {group.links.map(({ label, to, disabled }) =>
        disabled ? (
          <Typography
            key={label}
            variant="body2"
            sx={{
              opacity: 0.45,
              cursor: "not-allowed",
              fontSize: "0.85rem",
              userSelect: "none",
              color: "text.secondary",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            {label}
            <Box component="span" sx={{ fontSize: "0.62rem", ml: 0.7, px: 0.5, py: 0.1, bgcolor: "action.selected", borderRadius: 0.5, fontWeight: 700 }}>
              SOON
            </Box>
          </Typography>
        ) : (
          <Link key={label} to={to} className="footer-link">
            {label}
            {label.startsWith("View all") || label.startsWith("Explore") || label === "All legal policies" ? " →" : ""}
          </Link>
        )
      )}
    </Stack>
  );
}

export default function Footer() {
  const footerRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 899px)", { noSsr: true });

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer || typeof window === "undefined" || !window.IntersectionObserver) return undefined;
    const setChatVisibility = (visible) => {
      document.body.classList.toggle("footer-in-view", visible);
      if (window.Tawk_API && typeof window.Tawk_API[visible ? "hideWidget" : "showWidget"] === "function") window.Tawk_API[visible ? "hideWidget" : "showWidget"]();
    };
    const observer = new window.IntersectionObserver(([entry]) => setChatVisibility(entry.isIntersecting), { threshold: 0.08 });
    observer.observe(footer);
    return () => { observer.disconnect(); setChatVisibility(false); };
  }, []);

  return <Box component="footer" ref={footerRef} sx={{ bgcolor: "background.paper", color: "text.primary", borderTop: 1, borderColor: "divider", position: "relative", zIndex: 1 }}>
    <Container maxWidth={false} sx={{ maxWidth: 1440, py: { xs: 4, md: 6 }, px: { xs: 3, md: 4 }, pb: { xs: 5, md: 7 } }}>
      <Grid container spacing={{ xs: 3, md: 5 }} sx={{ mb: { xs: 4, md: 5 } }}>
        <Grid item xs={12} md={4} lg={3}>
          <Stack spacing={1.35} sx={{ maxWidth: 370 }}>
            <Typography variant="h5" fontWeight={800}>{company.name}</Typography>
            <Typography variant="subtitle2" color="primary.main">{company.positioning}</Typography>
            <Typography variant="body2" color="text.secondary">{company.description}</Typography>
            <Stack direction={{ xs: "column", sm: "row", md: "column", lg: "row" }} spacing={1} sx={{ pt: 0.75 }}>
              <Button component={Link} to="/contact-support/" variant="contained" size="small">Start a project</Button>
              <Button component="a" href={`https://wa.me/${company.whatsapp}`} target="_blank" rel="noreferrer" variant="outlined" size="small">Talk to Goafreet</Button>
            </Stack>
            <Typography variant="body2"><a className="footer-contact" href={`mailto:${company.email}`}>{company.email}</a><span aria-hidden="true"> · </span><a className="footer-contact" href={`https://wa.me/${company.whatsapp}`} target="_blank" rel="noreferrer">WhatsApp</a></Typography>
            <Stack direction="row" spacing={0.5}>{company.social.map(([label, href]) => { const Icon = socialIcons[label]; return <IconButton key={label} component="a" href={href} target="_blank" rel="noreferrer" size="small" color="primary" aria-label={label}><Icon /></IconButton>; })}</Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={8} lg={9} sx={{ display: { xs: "none", md: "block" } }}>
          <Grid container spacing={{ xs: 3, sm: 3, md: 4 }}>
            {serviceCategories.map((key) => <Grid key={key} item xs={6} sm={4} md={4} lg={2}><Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.2 }}>{footerNavigation[key].title}</Typography><FooterLinks group={footerNavigation[key]} /></Grid>)}
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ display: { xs: "none", md: "block" } }} />
      <Box sx={{ display: { xs: "none", md: "block" }, py: { xs: 3, md: 4 } }}>
        <Grid container spacing={3}>{secondaryFooterCategories.map((key) => <Grid key={key} item md={2}><Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.2 }}>{footerNavigation[key].title}</Typography><FooterLinks group={footerNavigation[key]} /></Grid>)}</Grid>
      </Box>
      <Box sx={{ display: { xs: "block", md: "none" }, py: 2, px: { xs: 0.5, sm: 0 } }}>{allCategories.map((key) => <Accordion key={key} disableGutters elevation={0} sx={{ bgcolor: "transparent", borderBottom: 1, borderColor: "divider", "&:before": { display: "none" } }}><AccordionSummary sx={{ px: 1 }} expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" fontWeight={800}>{footerNavigation[key].title}</Typography></AccordionSummary><AccordionDetails sx={{ pt: 0, pb: 2, px: 1 }}><FooterLinks group={footerNavigation[key]} /></AccordionDetails></Accordion>)}</Box>
      <Divider />
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1.5} sx={{ pt: 2.5 }}>
        <Typography variant="caption" color="text.secondary">© {new Date().getFullYear()} {company.name}. All rights reserved.</Typography>
        <WeatherClockWidget variant="footer" />
        <Stack direction="row" flexWrap="wrap" gap={1.5}><Link to="/sitemap/" className="footer-link">Sitemap</Link><Link to="/legal/privacy-policy/" className="footer-link">Privacy</Link><Link to="/legal/terms-of-use/" className="footer-link">Terms</Link><Link to="/legal/cookie-policy/" className="footer-link">Cookies</Link><Link to="/legal/" className="footer-link">Legal</Link></Stack>
      </Stack>
    </Container>
  </Box>;
}
